import { useQuery } from "@tanstack/react-query";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { kindLabel } from "@/lib/party-ledger";
import { EXPENSE_CATEGORIES, labelFor } from "@/features/expenses/constants";

export type MasterPartyType = "client" | "vendor" | "expense";

export type MasterLedgerEntry = {
  id: string;
  party_type: MasterPartyType;
  party_id: string;
  party_code: string;
  party_name: string;
  movement_date: string;
  kind: string;
  amount: number;
  reference: string | null;
  notes: string | null;
  /** High-level business bucket for filters / stats */
  bucket:
    | "sale"
    | "purchase"
    | "receipt"
    | "payment"
    | "credit"
    | "opening"
    | "adjustment"
    | "expense"
    | "other";
};

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function normalizeParty(raw: unknown): { id: string; code: string; name: string } | null {
  if (!raw) return null;
  const obj = Array.isArray(raw) ? raw[0] : raw;
  if (!obj || typeof obj !== "object") return null;
  const p = obj as { id?: string; code?: string; name?: string };
  if (!p.id) return null;
  return { id: p.id, code: p.code ?? "", name: p.name ?? "" };
}

function clientBucket(kind: string): MasterLedgerEntry["bucket"] {
  switch (kind) {
    case "opening":
      return "opening";
    case "invoice":
      return "sale";
    case "receipt":
      return "receipt";
    case "credit_note":
      return "credit";
    case "adjustment":
      return "adjustment";
    default:
      return "other";
  }
}

function vendorBucket(kind: string): MasterLedgerEntry["bucket"] {
  switch (kind) {
    case "opening":
      return "opening";
    case "bill":
      return "purchase";
    case "payment":
      return "payment";
    case "debit_note":
      return "credit";
    case "adjustment":
      return "adjustment";
    default:
      return "other";
  }
}

export function bucketLabel(bucket: MasterLedgerEntry["bucket"]) {
  const map: Record<MasterLedgerEntry["bucket"], string> = {
    sale: "Sale",
    purchase: "Purchase",
    receipt: "Receipt",
    payment: "Payment",
    credit: "Credit / debit note",
    opening: "Opening",
    adjustment: "Adjustment",
    expense: "Expense",
    other: "Other",
  };
  return map[bucket];
}

export function entryDescription(row: MasterLedgerEntry) {
  if (row.party_type === "expense") {
    return row.notes?.trim() || row.party_name;
  }
  return row.notes?.trim() || kindLabel(row.kind);
}

/** Company-wide ledger: client AR + vendor AP + expenses. */
export function useMasterLedger() {
  return useQuery({
    queryKey: ["master_ledger"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<MasterLedgerEntry[]> => {
      const [ar, ap, ex] = await Promise.all([
        supabase
          .from("client_ar_movements")
          .select("id, client_id, movement_date, kind, amount, reference, notes, clients(id, code, name)")
          .order("movement_date", { ascending: true }),
        supabase
          .from("vendor_ap_movements")
          .select("id, vendor_id, movement_date, kind, amount, reference, notes, vendors(id, code, name)")
          .order("movement_date", { ascending: true }),
        supabase
          .from("expenses")
          .select("id, expense_date, category, title, amount, reference, notes, payment_method")
          .order("expense_date", { ascending: true }),
      ]);

      if (ar.error) throw ar.error;
      if (ap.error) throw ap.error;
      if (ex.error) throw ex.error;

      const clientRows: MasterLedgerEntry[] = (ar.data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const party = normalizeParty(raw.clients);
        return {
          id: `ar-${raw.id as string}`,
          party_type: "client",
          party_id: raw.client_id as string,
          party_code: party?.code ?? "—",
          party_name: party?.name ?? "Unknown client",
          movement_date: raw.movement_date as string,
          kind: raw.kind as string,
          amount: money(raw.amount),
          reference: (raw.reference as string | null) ?? null,
          notes: (raw.notes as string | null) ?? null,
          bucket: clientBucket(raw.kind as string),
        };
      });

      const vendorRows: MasterLedgerEntry[] = (ap.data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const party = normalizeParty(raw.vendors);
        return {
          id: `ap-${raw.id as string}`,
          party_type: "vendor",
          party_id: raw.vendor_id as string,
          party_code: party?.code ?? "—",
          party_name: party?.name ?? "Unknown vendor",
          movement_date: raw.movement_date as string,
          kind: raw.kind as string,
          amount: money(raw.amount),
          reference: (raw.reference as string | null) ?? null,
          notes: (raw.notes as string | null) ?? null,
          bucket: vendorBucket(raw.kind as string),
        };
      });

      const expenseRows: MasterLedgerEntry[] = (ex.data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const category = raw.category as string;
        return {
          id: `ex-${raw.id as string}`,
          party_type: "expense",
          party_id: raw.id as string,
          party_code: category.toUpperCase().slice(0, 8),
          party_name: (raw.title as string) || labelFor(EXPENSE_CATEGORIES, category),
          movement_date: raw.expense_date as string,
          kind: category,
          // Positive → debit (cash out) in the shared debit/credit splitter
          amount: money(raw.amount),
          reference: (raw.reference as string | null) ?? null,
          notes: (raw.notes as string | null) ?? null,
          bucket: "expense",
        };
      });

      return [...clientRows, ...vendorRows, ...expenseRows].sort((a, b) => {
        const d = a.movement_date.localeCompare(b.movement_date);
        if (d !== 0) return d;
        return a.id.localeCompare(b.id);
      });
    },
  });
}
